import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useAccount } from '../../../contexts/AccountContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { updateClientProfile } from '../../../services/adminService';
import {
  ARCHETYPE_LABELS,
  type CollectionArchetype,
  DOMAIN_ARCHETYPES,
} from '../../../types/knowledge';
import SharepointOAuthDisconnect from '../../Auth/SharepointOAuthDisconnect';
import ManualSharepointWizard from '../../Sharepoint/ManualSharepointWizard';
import UpdateSharepointSecretModal from '../../Sharepoint/UpdateSharepointSecretModal';

const StoragePanel: React.FC = () => {
  const { account, refreshUserDetails } = useAccount();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const connectedSites = useMemo(() => {
    const sites = new Map<
      string,
      {
        siteId: string;
        driveId: string;
        url: string;
        name: string;
        isPrimary: boolean;
        collections: string[];
      }
    >();

    // Add primary site if exists
    if (account?.storageType === 'sharepoint' && account?.sharepointConfig?.siteUrl) {
      const siteId = (account.sharepointConfig.siteId ||
        account.sharepointConfig.defaultSiteId) as string;
      const driveId = (account.sharepointConfig.driveId ||
        account.sharepointConfig.defaultDriveId) as string;
      sites.set(siteId || 'primary', {
        siteId,
        driveId,
        url: account.sharepointConfig.siteUrl as string,
        name: 'Primary Storage (Processing Engine)',
        isPrimary: true,
        collections: ['Procurements & Processing'],
      });
    }

    // Add Knowledge Base sites
    if (account?.llm_settings) {
      const activeCollections = new Set(Object.values(DOMAIN_ARCHETYPES).flat());
      for (const [collection, value] of Object.entries(account.llm_settings)) {
        if (!activeCollections.has(collection as CollectionArchetype)) {
          continue;
        }

        const setting = value as {
          external_site?: { siteId?: string; driveId?: string; siteUrl?: string };
        };
        if (
          setting?.external_site?.siteId &&
          setting?.external_site?.driveId &&
          setting?.external_site?.siteUrl
        ) {
          const siteId = setting.external_site.siteId;
          if (sites.has(siteId)) {
            sites.get(siteId)?.collections.push(collection);
          } else {
            let name = setting.external_site.siteUrl;
            try {
              const urlObj = new URL(name);
              const match = urlObj.pathname.match(/^\/(sites|teams)\/([^/]+)/i);
              if (match?.[2]) {
                name = `${urlObj.hostname} - ${match[2]}`;
              } else {
                name = urlObj.hostname;
              }
            } catch {
              // ignore invalid URL
            }
            sites.set(siteId, {
              siteId,
              driveId: setting.external_site.driveId,
              url: setting.external_site.siteUrl,
              name,
              isPrimary: false,
              collections: [collection],
            });
          }
        }
      }
    }
    return Array.from(sites.values());
  }, [account]);

  const handleRemoveSite = async (siteId: string) => {
    if (!account?.clientId) {
      return;
    }

    const site = connectedSites.find((s) => s.siteId === siteId);
    if (!site || site.isPrimary) {
      return;
    }

    try {
      const newLlmSettings = JSON.parse(JSON.stringify(account.llm_settings || {}));

      // We must remove this site from all collections it is attached to in llm_settings
      for (const collection of site.collections) {
        if (newLlmSettings[collection]) {
          delete newLlmSettings[collection].external_site;
        }
      }

      await updateClientProfile(account.clientId, { llm_settings: newLlmSettings });
      await refreshUserDetails();
      showSnackbar('Site connection removed successfully.', 'success');
    } catch (_e) {
      showSnackbar('Failed to remove site connection.', 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Storage Configuration
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Your organization's files are managed in:{' '}
        <strong>
          {account?.storageType === 'sharepoint'
            ? 'Microsoft SharePoint'
            : 'BIDLOGIQ Cloud Storage (Australia - Sydney)'}
        </strong>
      </Typography>

      <Box sx={{ mb: 3 }}>
        {account?.storageType === 'sharepoint' ? (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                How Storage Works
              </Typography>
              <Typography variant="body2" paragraph>
                BIDLOGIQ uses two types of SharePoint sites to manage your data securely:
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold' }}>
                1. The Default Site (Storage & Processing)
              </Typography>
              {typeof account?.sharepointConfig?.siteUrl === 'string' &&
                account.sharepointConfig.siteUrl.trim() !== '' && (
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    <Link href={account.sharepointConfig.siteUrl} target="_blank" rel="noopener">
                      {account.sharepointConfig.siteUrl}
                    </Link>
                  </Typography>
                )}
              <Typography variant="body2" paragraph>
                When you upload files into BIDLOGIQ, they are saved and converted here. The AI uses
                this site to store its generated drafts and data extraction results. To prevent
                system errors, <strong>do not manually edit or delete files</strong> directly inside
                this SharePoint site. Always manage them through the BIDLOGIQ interface.
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold' }}>
                2. Knowledge Base Connections
              </Typography>
              <Typography variant="body2">
                These are your existing SharePoint sites where your team already works. By
                connecting them below, you give the AI secure, read-only access to your company's
                past proposals, policies, and CVs. The AI reads these files to write better drafts
                without needing you to copy them into the Default Site.
              </Typography>
            </Paper>
            {connectedSites.filter((s) => !s.isPrimary).length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Knowledge Base Connections
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Site Name / URL</TableCell>
                        <TableCell>Collections</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {connectedSites
                        .filter((s) => !s.isPrimary)
                        .map((site) => (
                          <TableRow key={site.siteId || site.url}>
                            <TableCell>
                              <Typography variant="body2">{site.name}</Typography>
                              <Link
                                href={site.url}
                                target="_blank"
                                rel="noopener"
                                variant="caption"
                                sx={{ wordBreak: 'break-all' }}
                              >
                                {site.url}
                              </Link>
                            </TableCell>
                            <TableCell>
                              {site.collections.map((c) => (
                                <Chip
                                  key={c}
                                  label={ARCHETYPE_LABELS[c as CollectionArchetype] || c}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    color: 'text.primary',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    fontWeight: 'medium',
                                  }}
                                />
                              ))}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Remove Connection">
                                <IconButton
                                  size="large"
                                  color="error"
                                  onClick={() => handleRemoveSite(site.siteId)}
                                >
                                  <DeleteIcon fontSize="medium" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
              Advanced Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={() => setIsUpdateModalOpen(true)}
                >
                  Update Client Secret
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SharepointOAuthDisconnect redirectPath="/admin?tab=storage" />
              </Grid>
            </Grid>
            <UpdateSharepointSecretModal
              open={isUpdateModalOpen}
              onClose={() => setIsUpdateModalOpen(false)}
            />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            {!showSetup ? (
              <Button variant="contained" color="primary" onClick={() => setShowSetup(true)}>
                Connect to SharePoint
              </Button>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, width: '100%' }}>
                <Typography variant="h6" gutterBottom align="left">
                  Connect to SharePoint
                </Typography>
                <ManualSharepointWizard onSuccess={refreshUserDetails} />
                <Button variant="text" sx={{ mt: 2 }} onClick={() => setShowSetup(false)}>
                  Cancel
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Box>
      <Typography sx={{ textAlign: 'center', mt: 4 }}>
        <strong>Need a different storage region or platform? Contact us! </strong>
        <br />
        <strong>Email: </strong>{' '}
        <Link
          href="mailto:support@bidlogiq.ai"
          color="text.primary"
          underline="always"
          sx={{ fontWeight: 'bold' }}
        >
          support@bidlogiq.ai
        </Link>
        <br />
        <strong>AUS: </strong>+61 (0)7 2143 6035
        <br />
      </Typography>
    </Box>
  );
};

export default StoragePanel;
